import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './WorkTasks.css';
import TaskListView from './TaskListView';
import BoardView from './BoardView';
import TaskDetail from './TaskDetail';
import TaskForm from './TaskForm';
import StudentPicker from './StudentPicker';
import ProjectDetail from './ProjectDetail';
import ProjectForm from './ProjectForm';
import SortMenu from './SortMenu';
import SortFilterSheet from './SortFilterSheet';
import WorkSidebar from './WorkSidebar';
import {
	buildTaskTree,
	isOpen,
	isCompleted,
	needsInfo,
	needsTriage,
	nextStatus,
	areaOrderFor,
	areaMeta,
	GATE_ORDER,
	PEOPLE_BY_UID,
	SORT_OPTIONS,
	DEFAULT_SORT,
	statusDrop,
} from './taskModel';
import { toPlainText } from './cleanMarkdown';
import { Avatar } from './Avatar';
// taskChangeState is a PURE helper — it touches no collection, so it stays a
// direct import. Routing it through the service context would imply it varies
// per space, which it does not.
import { taskChangeState } from '../../../services/TaskService';
import { useTaskService } from '../../../contexts/TaskServiceContext';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { useProjectService } from '../../../contexts/ProjectServiceContext';
import { subscribeToMyPrefs, saveMyPrefs } from '../../../services/UserPrefsService';
import { catalogFor, defaultsFor, normalizeProperties } from './cardProperties';
import { spaceFields, boardPrefsNamespace, isGateOffered } from './spaceFields';
import { useIsMobile } from './useIsMobile';
import { SPACES } from '../portalSpaces';
import AccessNotice from '../AccessNotice';
import { isPermissionDenied } from '../../../utils/permissions';

// Portal Phase C — Work tasks module (orchestrator).
// Owns the live data (teacherTasks + projects), the active view, search + filter
// state, and the shared task tree. Renders inside PortalSpaceChrome (.portal-shell)
// so it inherits the portal-scoped Comfort theme. C1: list shell + view switcher
// + inline status-advance + empty states. (Board → C2, detail/quick-add → C3,
// project detail → C4 extend this same shell.)

const VIEWS = [
	{ key: 'needs', label: 'Needs action', icon: 'fa-list-check' },
	{ key: 'board', label: 'Board', icon: 'fa-table-columns' },
	{ key: 'area', label: 'By area', icon: 'fa-layer-group' },
	{ key: 'project', label: 'By project', icon: 'fa-folder-tree' },
	{ key: 'today', label: 'Today', icon: 'fa-calendar-day' },
];

// Module-level so the "no projects here" case is a stable reference, and cannot
// re-fire a memo that depends on it.
const EMPTY_PROJECTS = [];

const SECTION_HINT = {
	needs: 'Priority · due',
	board: 'Drag to move',
	area: 'Grouped by work-type',
	project: 'Grouped by project',
	today: 'Overdue → later',
};

// `assigneeUid` (optional): when set, the module shows ONLY the tasks that user
// is ON (assigned, @mentioned, or created by her; the teacher dashboard's
// scoped variant) instead of all tasks. The
// scoped query is required, not cosmetic — E1 rules deny a non-admin's read of
// tasks she isn't on, so an unscoped subscription would error for her.
function WorkTasksModule({ tasksOverride, projectsOverride, assigneeUid }) {
	const injected = Array.isArray(tasksOverride);
	// Read markers key off the VIEWER, not the assignee scope — an admin browsing
	// the full board still wants "changed since I looked" on cards assigned to
	// nobody. assigneeUid only controls which tasks are subscribed to.
	const { currentUser } = useAuth();
	// Which space's collection this module reads and writes. Defaults to Work
	// when no provider wraps the tree, so /portal/work, the teacher dashboard
	// and the admin home are all unaffected by the split.
	const {
		subscribeToAllTasks,
		subscribeToVisibleTasks,
		updateTaskStatus,
		updateTask,
		subscribeToMyTaskReads,
		markTaskRead,
		space,
	} = useTaskService();
	// Which studio fields this space offers, and the vocabulary that follows from
	// it. Every value here derives from `space` alone, which is fixed for the life
	// of a mount (the provider's value is a module-level constant), so none of
	// these can change under a running board.
	const fields = spaceFields(space);
	/* The space's projects binding. Read-only in Work by construction (see
	   ProjectService.js), writable in Personal — `projectService.writable` is
	   what the create affordance below keys off, rather than the space name, so
	   enabling another space is a service change and not a component change. */
	const projectService = useProjectService();
	const views = useMemo(
		() => VIEWS.filter((v) => v.key !== 'project' || fields.project),
		[fields]
	);
	const propertyCatalog = useMemo(() => catalogFor(fields), [fields]);
	const offeredGates = useMemo(
		() => GATE_ORDER.filter((g) => isGateOffered(fields, g)),
		[fields]
	);
	const offeredAreas = useMemo(() => areaOrderFor(fields), [fields]);
	// The card-property choice is stored PER SPACE. It used to be one shared
	// `workBoard` namespace, which meant turning Area off in Personal turned it
	// off in Work too — the two surfaces were writing over each other through a
	// single document field. Work keeps the exact namespace it already had, so no
	// existing preference moves.
	const prefsNamespace = boardPrefsNamespace(space);
	const spaceName = SPACES[space]?.name || 'Work';
	const viewerUid = currentUser?.uid || null;
	const [taskReads, setTaskReads] = useState({});
	const [tasks, setTasks] = useState(injected ? tasksOverride : []);
	const [loadedProjects, setProjects] = useState(projectsOverride || []);
	const [loading, setLoading] = useState(!injected);
	// A non-admin who reaches this route unscoped (no assigneeUid) has their
	// all-tasks read denied by rules — render an access state instead of an
	// infinite loading skeleton (#26).
	const [denied, setDenied] = useState(false);
	const [view, setView] = useState('needs');
	const [search, setSearch] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const [filtersOpen, setFiltersOpen] = useState(false);
	// Mobile Sort & filter sheet (the sliders button's surface under 768px).
	const [sheetOpen, setSheetOpen] = useState(false);
	const isMobile = useIsMobile();
	const [areaFilter, setAreaFilter] = useState(() => new Set());
	const [assigneeFilter, setAssigneeFilter] = useState(() => new Set());
	const [parkedOnly, setParkedOnly] = useState(false);
	// Triage signals (Part E): raw captures (triaged === false) and tasks the
	// triage skill flagged it couldn't fully resolve (needsInfo === true).
	// Both are filtered through their taskModel predicates, not the raw flags —
	// a closed task is neither, whatever its flags still say.
	const [needsTriageOnly, setNeedsTriageOnly] = useState(false);
	const [needsInfoOnly, setNeedsInfoOnly] = useState(false);
	// "Mail only" — cards captured from email (Anthony, 2026-09-05: "separate
	// mail from other things"). A source filter, not a status one: it is
	// applied here like the triage chips so list and board agree.
	const [mailOnly, setMailOnly] = useState(false);
	const [boardGroupBy, setBoardGroupBy] = useState('status');
	// Which properties render on board cards (Anthony's call, 2026-07-26).
	// Seeded with the defaults rather than empty so the first paint is a
	// well-formed card, not a bare title that fills in a beat later — the
	// subscription below replaces it once the user's own choice arrives.
	const [cardProps, setCardProps] = useState(() => defaultsFor(fields));
	// Completed work: HIDDEN by default, per user, persisted in the same prefs
	// namespace as the card-property choice. Seeded false rather than from the
	// subscription so the first paint is the default view; the user's own choice
	// arrives a tick later and replaces it, exactly as cardProps does.
	//
	// GLOBAL SINCE 2026-08-26 (Anthony: "I need a way to hide tasks that are
	// completed in every view"). It used to govern only the Board's Completed
	// column. The list views already hid completed unconditionally, so the leaks
	// were the places nothing filtered at all: sub-task children under an open
	// parent, the project rollup's Completed section, and the mention picker.
	// One preference now answers for all of them — the same reason needsTriage
	// and needsInfo were moved into taskModel: a display question answered in
	// several places gets several answers.
	const [showCompleted, setShowCompleted] = useState(false);
	// Task-list sort control (v1: component state, resets on reload — no
	// persistence yet). Board view keeps its own status/area/assignee grouping
	// and ignores this.
	const [sortBy, setSortBy] = useState(DEFAULT_SORT);
	// Overlays (C3): task detail, create/edit form, student picker.
	const [detailId, setDetailId] = useState(null);
	const [form, setForm] = useState(null); // { initial?, parentId?, defaultProjectId? } | null
	const [pickerTask, setPickerTask] = useState(null);
	// Task dropped on Parked, waiting for the human to say what it is waiting ON.
	// Held here rather than in BoardView because the write lives here; BoardView
	// only renders the choice.
	const [parkPrompt, setParkPrompt] = useState(null);
	const [projectDetailId, setProjectDetailId] = useState(null);
	const [projectForm, setProjectForm] = useState(false);

	// Deep-link: a push notification / Scriptable widget row links to
	// /portal/work?task=<id>. Open that task's existing TaskDetail once the live
	// data has settled and a matching task is in scope.
	const [searchParams, setSearchParams] = useSearchParams();

	// One clock fixed at mount for due/overdue calc + sorting (stable reference).
	const now = useMemo(() => new Date(), []);

	useEffect(() => {
		// Injected data (dev harness / tests) skips the live subscriptions.
		if (injected) return undefined;
		const onRows = (rows) => {
			setTasks(rows);
			setLoading(false);
			setDenied(false);
		};
		const onErr = (err) => {
			setLoading(false);
			setDenied(isPermissionDenied(err));
		};
		// Teacher dashboard passes assigneeUid → every task she is ON (assigned,
		// @mentioned, or created by her; rules isOnTask); admins/owners get the
		// full board. subscribeToVisibleTasks already has its own error handling;
		// the unscoped all-tasks read needs onErr so a
		// non-admin's denial resolves instead of spinning forever (#26).
		const unsubTasks = assigneeUid
			? subscribeToVisibleTasks(assigneeUid, onRows)
			: subscribeToAllTasks(onRows, onErr);
		/* Projects come from the SPACE's own collection, resolved through the
		   project service context — Work reads `projects`, Personal reads
		   `personalProjects`. This used to be a module-level import of the Work
		   subscriber, which is a hardcoded collection by another name: with the
		   project field now enabled in Personal, that would have listed the five
		   studio projects in the household rail while writing elsewhere.

		   THE `space` MATCH IS A REAL GUARD, not a belt-and-braces assertion, and
		   it is the price of turning `project` back on for a private space. The
		   project context DEFAULTS to the Work binding — which is the right
		   default, and is what keeps every existing Work mount unchanged — but it
		   means a subtree that binds the task service and FORGETS the project one
		   would pair Personal's tasks with the studio's projects. That is the
		   same class of leak the collection wall exists to prevent, arriving by
		   omission instead of by import. Requiring the two services to agree on
		   their space turns that mistake into NO projects, which is visible and
		   harmless, instead of the wrong ones, which is neither.

		   The `fields.project` guard stays alongside it: a space that does not
		   offer the field must hold no listener at all. Owners and Barbara are
		   stopped by that clause, Personal-without-a-provider by this one. */
		const unsubProjects =
			fields.project && projectService.space === space
				? projectService.subscribeToProjects(setProjects)
				: () => {};
		// Card-property choice. normalizeProperties separates 'never written'
		// (-> defaults) from 'deliberately empty' (-> a title-only card), which
		// are indistinguishable once the value reaches the card.
		const unsubPrefs = viewerUid
			? subscribeToMyPrefs(viewerUid, (prefs) => {
					setCardProps(
						normalizeProperties(prefs?.[prefsNamespace]?.properties, fields)
					);
					// `=== true` on purpose: an unwritten preference and a missing
					// document are both 'hidden', which IS the default — there is
					// nothing here to separate the way normalizeProperties has to.
					setShowCompleted(prefs?.[prefsNamespace]?.showCompleted === true);
			  })
			: () => {};
		// Own read markers. Self-scoped by rules, so this never fails for a
		// signed-in user; a denial here must not blank the board, hence the
		// no-op error path (worst case every card reads as unread, which is the
		// safe direction).
		const unsubReads = viewerUid
			? subscribeToMyTaskReads(viewerUid, setTaskReads, () => setTaskReads({}))
			: () => {};
		return () => {
			unsubTasks();
			unsubProjects();
			unsubPrefs();
			unsubReads();
		};
		// The subscribe fns come from the service contexts, whose values are
		// module-level constants — referentially stable, so listing them here
		// cannot cause this subscription to re-fire. `projectService` and `space`
		// are listed for the same reason and are declared rather than suppressed:
		// both are fixed for the life of a mount, so the honest dependency list
		// and the disabled-lint version behave identically — and the honest one
		// keeps telling the truth if either ever stops being constant.
	}, [
		injected,
		assigneeUid,
		viewerUid,
		fields,
		space,
		prefsNamespace,
		projectService,
		subscribeToVisibleTasks,
		subscribeToAllTasks,
		subscribeToMyTaskReads,
	]);

	// The Sort & filter sheet is a mobile-only surface — if the viewport grows
	// past the breakpoint while it's open (rotate / resize), drop it so it can't
	// linger over the desktop layout.
	useEffect(() => {
		if (!isMobile && sheetOpen) setSheetOpen(false);
	}, [isMobile, sheetOpen]);

	// Consume ?task=<id> once the live subscription has settled (loading=false).
	// Open the task's detail when it's present in the loaded set; in assigneeUid
	// scoped mode a task the user isn't on simply won't match → no open, no error.
	// Either way clear the param (replace) so closing the detail can't loop-reopen
	// it and a stale param doesn't linger in the URL.
	useEffect(() => {
		const taskId = searchParams.get('task');
		if (!taskId || loading) return;
		if (tasks.some((t) => t.id === taskId)) setDetailId(taskId);
		setSearchParams({}, { replace: true });
	}, [loading, tasks, searchParams, setSearchParams]);

	/* ONE choke point for the project field, rather than a check at each of the
	   five places projects flow (the rail, the list view, the form, the detail
	   overlay, the by-id map). Gating per render site is how one of them gets
	   missed; gating the value means a space without the field cannot surface a
	   project through ANY of them — including from `projectsOverride`, which a
	   harness can inject regardless of what the subscription did. */
	const projects = fields.project ? loadedProjects : EMPTY_PROJECTS;

	const projectsById = useMemo(() => {
		const m = {};
		for (const p of projects) m[p.id] = p;
		return m;
	}, [projects]);

	const { roots, childrenByParent } = useMemo(
		() => buildTaskTree(tasks),
		[tasks]
	);

	/* Areas actually present (for the filter bar), in this SPACE's order — the
	   studio seven in Work, the space's own list in Personal/Owners/Barbara.
	   Reading AREA_ORDER here would have silently dropped every private area
	   from the filter bar, since none of their keys are in the studio list.

	   PRESENT-BUT-NOT-OFFERED AREAS ARE APPENDED, not dropped, and that trailing
	   clause is not tidiness. Every task in a private space predates that space's
	   own vocabulary, so a Personal task can genuinely carry `musicPrep`. The
	   By-area grouping already shows it (groupBy appends keys outside the order
	   it is given), so a plain intersection here left the two surfaces disagreeing
	   about which areas exist: a group you can see and cannot filter to. Same rule
	   as the form's already-set-value clause — narrowing what can be CHOSEN must
	   not narrow what can be SEEN. Found by rendering the page; no unit test was
	   comparing the two surfaces to each other. */
	const areasInUse = useMemo(() => {
		const present = new Set(tasks.map((t) => t.area).filter(Boolean));
		const offered = offeredAreas.filter((a) => present.has(a));
		const extra = [...present].filter((a) => !offeredAreas.includes(a));
		return [...offered, ...extra];
	}, [tasks, offeredAreas]);

	const peopleInUse = useMemo(() => {
		const uids = new Set();
		for (const t of tasks)
			for (const u of t.assigneeUids || []) uids.add(u);
		return [...uids].map(
			(uid) => PEOPLE_BY_UID[uid] || { uid, name: 'Teammate', initials: '?', colorVar: '--person-annelise' }
		);
	}, [tasks]);

	// Shared filter predicate (search + area + assignee + parked) — minus the
	// open-work restriction, which only the list views apply.
	const matchesFilters = useMemo(() => {
		const q = search.trim().toLowerCase();
		return (t) => {
			if (parkedOnly && !t.gate) return false;
			// Both triage chips carry two halves (the flag AND still open) — see
			// their comments in taskModel.js. They have to be applied HERE rather
			// than left to the views, because the board deliberately skips
			// isOpen() so its Completed column can populate, which had these
			// chips answering differently depending on which view you were in.
			if (needsTriageOnly && !needsTriage(t)) return false;
			if (needsInfoOnly && !needsInfo(t)) return false;
			if (mailOnly && t.source !== 'email') return false;
			if (areaFilter.size && !areaFilter.has(t.area)) return false;
			if (assigneeFilter.size) {
				const has = (t.assigneeUids || []).some((u) =>
					assigneeFilter.has(u)
				);
				if (!has) return false;
			}
			if (q) {
				const hay = `${t.title || ''} ${toPlainText(t.description)}`.toLowerCase();
				if (!hay.includes(q)) return false;
			}
			return true;
		};
	}, [search, parkedOnly, needsTriageOnly, needsInfoOnly, mailOnly, areaFilter, assigneeFilter]);

	// List views: open work passing the filters, PLUS completed work when the
	// viewer has asked to see it.
	//
	// Written as `isOpen(t) || (showCompleted && isCompleted(t))` rather than
	// `showCompleted || isOpen(t)` on purpose: a task whose status is missing or
	// unrecognised is neither open nor completed, and the second form would show
	// it only when completed are shown — i.e. a broken row would hide itself
	// behind a preference about finished work. This form keeps it out of both.
	const visibleRoots = useMemo(
		() => roots.filter((t) => (isOpen(t) || (showCompleted && isCompleted(t))) && matchesFilters(t)),
		[roots, matchesFilters, showCompleted]
	);
	// Board: ALL statuses, always, regardless of the preference. The Completed
	// column is a drop target for drag-to-complete (BoardView renders it even
	// when its cards are hidden), so filtering completed out here would break
	// dropping and make the column's count lie. BoardView applies the preference
	// itself, to the cards only.
	const boardRoots = useMemo(
		() => roots.filter(matchesFilters),
		[roots, matchesFilters]
	);

	const openCount = view === 'board' ? boardRoots.length : visibleRoots.length;
	const detailTask = detailId ? tasks.find((t) => t.id === detailId) : null;
	const detailProject = projectDetailId
		? projects.find((p) => p.id === projectDetailId)
		: null;
	const anyChipFilter =
		parkedOnly ||
		needsTriageOnly ||
		needsInfoOnly ||
		mailOnly ||
		areaFilter.size > 0 ||
		assigneeFilter.size > 0;
	const anyFilter = anyChipFilter || !!search.trim();
	const clearFilters = () => {
		setAreaFilter(new Set());
		setAssigneeFilter(new Set());
		setParkedOnly(false);
		setNeedsTriageOnly(false);
		setNeedsInfoOnly(false);
		setMailOnly(false);
	};

	// Per-row "has this moved since I looked?" — pure lookup, no extra reads:
	// subscribeToAllTasks already maps updatedAt onto every task, and
	// commentCount is denormalized on the doc.
	const changeFor = useCallback(
		(task) => taskChangeState(task, taskReads[task.id]),
		[taskReads]
	);

	// Any write from THIS client re-marks the card read, so you never get a
	// "changed" dot for your own edit. Agent writes (MCP, Cloud Functions) never
	// call this, which is exactly why their changes do leave a dot — that is the
	// signal this whole feature exists to surface.
	const seen = (task) => {
		if (viewerUid && task?.id) markTaskRead(viewerUid, task.id, task.commentCount ?? 0);
	};

	const handleAdvance = async (task, target) => {
		try {
			await updateTaskStatus(task.id, target || nextStatus(task.status));
			seen(task);
		} catch (e) {
			console.error('Failed to advance task status:', e);
		}
	};

	const handleOpen = (task) => {
		setDetailId(task.id);
		seen(task);
	};
	const handleEdit = (task) => {
		setDetailId(null);
		setForm({ initial: task });
	};
	const handleReprioritize = (task, priority) =>
		updateTask(task.id, { priority })
			.then(() => seen(task))
			.catch((e) => console.error('Failed to reprioritize:', e));
	// Row-level quick edits — the phone action bar AND the desktop due cell.
	// `update` is the raw field object the chosen option carries (quickActions.js
	// builds it), so date / assignee / priority / area / park all land here
	// rather than needing five near-identical handlers.
	const handleQuickUpdate = (task, update) =>
		updateTask(task.id, update)
			.then(() => seen(task))
			.catch((e) => console.error('Failed to update task:', e));
	// DueQuickSet keeps a date-shaped API of its own; it is the same write.
	const handleSetDeadline = (task, date) =>
		handleQuickUpdate(task, { deadline: date || null });
	const handleReassign = (task, uid) => {
		const person = uid ? PEOPLE_BY_UID[uid] : null;
		updateTask(task.id, {
			assigneeUids: uid ? [uid] : [],
			assignedTo: person ? [person.name] : [],
		})
			.then(() => seen(task))
			.catch((e) => console.error('Failed to reassign:', e));
	};
	const handleAddSubtask = (task) => setForm({ parentId: task.id, defaultProjectId: task.projectId });
	const handleOpenProject = (project) => setProjectDetailId(project.id);
	// Optimistic: the local state moves first so a toggle feels instant, and the
	// write follows. A failed write is swallowed on purpose — the onSnapshot
	// above is the source of truth and will correct the UI on the next tick, and
	// a card-layout preference is never worth an error dialog. Nothing is lost
	// that a second click cannot redo.
	const handleCardPropsChange = (next) => {
		setCardProps(next);
		if (viewerUid) {
			saveMyPrefs(viewerUid, prefsNamespace, { properties: next }).catch(() => {});
		}
	};
	// Same optimistic shape as the card-property write above. saveMyPrefs uses
	// setDoc(merge:true), which deep-merges MAP fields, so writing showCompleted
	// alone leaves the sibling `properties` array in this namespace untouched —
	// that is what lets a second preference join the existing document instead of
	// spawning a mechanism of its own.
	const handleShowCompletedChange = (next) => {
		setShowCompleted(next);
		if (viewerUid) {
			saveMyPrefs(viewerUid, prefsNamespace, { showCompleted: next }).catch(() => {});
		}
	};
	// Picking a view leaves the project. The project panel now occupies the same
	// column the views render into, so without this, choosing "Board" would swap
	// the header underneath a panel that stayed put — the old modal could ignore
	// this because it floated above the whole surface.
	const selectView = (next) => {
		setProjectDetailId(null);
		setView(next);
	};
	const handleAddToProject = (project) => setForm({ defaultProjectId: project.id });
	const handleLinkStudent = (task) => setPickerTask(task);
	const handleStudentSelected = (student) => {
		if (pickerTask) {
			updateTask(pickerTask.id, {
				studentId: student.id,
				studentName: student.name,
			}).catch((e) => console.error('Failed to link student:', e));
		}
		setPickerTask(null);
	};

	// Board drop → write the grouped dimension. closedAt is left to the
	// stampClosedDate trigger when status crosses to/from completed.
	const handleMove = async (task, groupBy, targetKey) => {
		try {
			if (groupBy === 'status') {
				// The Parked lane makes a status drop non-trivial, so the rule lives
				// in taskModel where it is testable. Parking asks for the reason
				// instead of writing — see statusDrop.
				const decision = statusDrop(task, targetKey);
				if (decision.action === 'none') return;
				if (decision.action === 'choose-gate') {
					setParkPrompt(task);
					return;
				}
				await updateTask(task.id, decision.update);
			} else if (groupBy === 'area') {
				await updateTask(task.id, { area: targetKey });
			} else if (groupBy === 'assignee') {
				if (targetKey === 'unassigned') {
					await updateTask(task.id, { assigneeUids: [], assignedTo: [] });
				} else {
					const person = PEOPLE_BY_UID[targetKey];
					await updateTask(task.id, {
						assigneeUids: [targetKey],
						assignedTo: person ? [person.name] : [],
					});
				}
			}
		} catch (e) {
			console.error('Failed to move task:', e);
		}
	};

	// The second half of a park: the human has said what the card is waiting on.
	// Only the gate is written — status is left alone, because the drag said
	// "park this" and nothing else, and rewriting a second field would be a
	// consequence the gesture never named.
	const handleParkChoose = async (gate) => {
		const task = parkPrompt;
		setParkPrompt(null);
		if (!task || !gate) return;
		try {
			await updateTask(task.id, { gate });
			seen(task);
		} catch (e) {
			console.error('Failed to park task:', e);
		}
	};

	const toggleSetItem = (setter, value) => {
		setter((prev) => {
			const next = new Set(prev);
			if (next.has(value)) next.delete(value);
			else next.add(value);
			return next;
		});
	};

	const emptyState = (() => {
		if (anyFilter) {
			return (
				<div className="wt-empty">
					<span className="wt-empty__bubble">
						<i className="fa-sharp-duotone fa-solid fa-filter-slash" />
					</span>
					<div className="wt-empty__title">No tasks match</div>
					<div className="wt-empty__sub">
						Try clearing a filter or search to see more work.
					</div>
				</div>
			);
		}
		return (
			<div className="wt-empty">
				<span className="wt-empty__bubble">
					<i className="fa-sharp-duotone fa-solid fa-mug-hot" />
				</span>
				<div className="wt-empty__title">You're all caught up</div>
				<div className="wt-empty__sub">
					Nothing needs action right now. New work shows up here as it
					comes in.
				</div>
			</div>
		);
	})();

	return (
		// The 1180px cap on .wt is a READING measure — right for the list views,
		// wrong for a kanban board, which is the one view whose content is laid
		// out across the screen rather than down it. Without this the fourth
		// column could never fit at any window size: the module ran out of width
		// before the viewport did.
		<div className={`wt${view === 'board' ? ' wt--board' : ''}`}>
			{/* App bar */}
			<div className="wt-appbar">
				{searchOpen ? (
					<div className="wt-search">
						<i className="fa-sharp fa-solid fa-magnifying-glass" />
						{/* eslint-disable-next-line jsx-a11y/no-autofocus */}
						<input
							autoFocus
							value={search}
							placeholder="Search tasks…"
							onChange={(e) => setSearch(e.target.value)}
						/>
						{/* Its OWN class, not the row chevron's. Borrowing wt-row__chev
						    brought along that class's `opacity: 0` hover-reveal, whose
						    only un-hiding selector is `.wt-row:hover` — and this button
						    is not inside a .wt-row, so on every screen at or above 768px
						    it was invisible until keyboard focus. Anthony reported it as
						    a colour problem; the colour was never the cause. */}
						<button
							type="button"
							className="wt-search__clear"
							aria-label="Close search"
							onClick={() => {
								setSearch('');
								setSearchOpen(false);
							}}>
							<i className="fa-sharp fa-solid fa-xmark" />
						</button>
					</div>
				) : (
					<>
						<div className="wt-appbar__titles">
							<span className="wt-appbar__eyebrow">{spaceName}</span>
							<h1 className="wt-appbar__title">
								Tasks
								<span className="wt-appbar__count">
									{openCount} open
								</span>
							</h1>
						</div>
						<button
							type="button"
							className="wt-iconbtn"
							aria-label="Search"
							onClick={() => setSearchOpen(true)}>
							<i className="fa-sharp fa-solid fa-magnifying-glass" />
						</button>
						<button
							type="button"
							className={`wt-iconbtn${
								filtersOpen || sheetOpen || anyFilter ? ' is-active' : ''
							}`}
							aria-label="Sort and filter"
							aria-haspopup={isMobile ? 'dialog' : undefined}
							aria-pressed={isMobile ? undefined : filtersOpen}
							aria-expanded={isMobile ? sheetOpen : undefined}
							onClick={() =>
								isMobile
									? setSheetOpen(true)
									: setFiltersOpen((v) => !v)
							}>
							<i className="fa-sharp fa-solid fa-sliders" />
						</button>
					</>
				)}
			</div>

			{/* Desktop rail (>=980px) + main column. On mobile .wt-body and
			    .wt-main collapse to display:contents so children flow in the .wt
			    flex column exactly as before; the sidebar hides and the pills
			    below drive the view. */}
			<div className="wt-body">
				<WorkSidebar
					views={views}
					spaceName={spaceName}
					view={view}
					setView={selectView}
					openCount={visibleRoots.length}
					projects={projects}
					allTasks={tasks}
					onOpenProject={handleOpenProject}
				/>
				<div className="wt-main">
			{/* View switcher */}
			<div className="wt-switcher" role="tablist" aria-label="Task views">
				{views.map((v) => (
					<button
						key={v.key}
						type="button"
						role="tab"
						aria-selected={view === v.key}
						className={`wt-switcher__pill${view === v.key ? ' is-active' : ''}`}
						onClick={() => selectView(v.key)}>
						<i className={`fa-sharp fa-solid ${v.icon}`} />
						{v.label}
					</button>
				))}
			</div>

			{/* Filter bar — removable chips (✕ when active) + Clear. The sliders
			    button toggles this panel; behaviour (area / who / gate filters) is
			    unchanged from the shipped control. */}
			{filtersOpen && (
				<div className="wt-filters">
					<span className="wt-filter__label">Area</span>
					{areasInUse.map((a) => {
						const on = areaFilter.has(a);
						return (
							<button
								key={a}
								type="button"
								className={`wt-filter${on ? ' is-active' : ''}`}
								onClick={() => toggleSetItem(setAreaFilter, a)}>
								<span
									className="wt-tag__dot"
									style={{ '--row-accent': `var(--area-${a})` }}
								/>
								{areaMeta(a).label}
								{on && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
							</button>
						);
					})}
					{peopleInUse.length > 0 && (
						<span className="wt-filter__label">Who</span>
					)}
					{peopleInUse.map((p) => {
						const on = assigneeFilter.has(p.uid);
						return (
							<button
								key={p.uid}
								type="button"
								className={`wt-filter wt-filter--who${on ? ' is-active' : ''}`}
								onClick={() => toggleSetItem(setAssigneeFilter, p.uid)}>
								<Avatar uid={p.uid} size={24} name={p.name} />
								{p.name}
								{on && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
							</button>
						);
					})}
					{/* Flag toggles break to their own row beneath the Area / Who groups. */}
					<div className="wt-filter__flags">
						<button
							type="button"
							className={`wt-filter${parkedOnly ? ' is-active' : ''}`}
							onClick={() => setParkedOnly((v) => !v)}>
							<i className="fa-sharp fa-solid fa-circle-pause" />
							Parked
							{parkedOnly && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
						</button>
						<button
							type="button"
							className={`wt-filter${needsTriageOnly ? ' is-active' : ''}`}
							onClick={() => setNeedsTriageOnly((v) => !v)}>
							<i className="fa-sharp fa-solid fa-inbox" />
							Needs triage
							{needsTriageOnly && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
						</button>
						<button
							type="button"
							className={`wt-filter${needsInfoOnly ? ' is-active' : ''}`}
							onClick={() => setNeedsInfoOnly((v) => !v)}>
							<i className="fa-sharp fa-solid fa-circle-question" />
							Needs info
							{needsInfoOnly && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
						</button>
						<button
							type="button"
							className={`wt-filter${mailOnly ? ' is-active' : ''}`}
							onClick={() => setMailOnly((v) => !v)}>
							<i className="fa-sharp fa-solid fa-envelope" />
							Mail
							{mailOnly && <i className="fa-sharp fa-solid fa-xmark wt-filter__x" />}
						</button>
						{/* Show completed — a persisted DISPLAY preference, not a
						    filter chip, which is why it is deliberately absent from
						    clearFilters() below and from anyChipFilter: "Clear"
						    should not silently flip a setting the user chose once
						    and expects to stay put. It reads as active when
						    completed are SHOWN, since hidden is the default. */}
						<button
							type="button"
							className={`wt-filter${showCompleted ? ' is-active' : ''}`}
							aria-pressed={showCompleted}
							onClick={() => handleShowCompletedChange(!showCompleted)}>
							<i className={`fa-sharp fa-solid ${showCompleted ? 'fa-eye' : 'fa-eye-slash'}`} />
							{showCompleted ? 'Completed shown' : 'Show completed'}
						</button>
						{anyChipFilter && (
							<button
								type="button"
								className="wt-filter__clear"
								onClick={clearFilters}>
								Clear
							</button>
						)}
					</div>
				</div>
			)}

			{/* Section header. When a project is open the header names the PROJECT
			    rather than the view — the panel below has replaced the list, so a
			    header still reading "By project" would describe something that is
			    no longer on screen. The sort control stays mounted either way,
			    which is the whole point of moving the panel into this column: the
			    project's tasks are sorted by the same control as every other list,
			    instead of by a hardcoded order with no way to change it. */}
			<div className="wt-sectionhead">
				<span className="wt-sectionhead__title">
					{detailProject
						? detailProject.title
						: views.find((v) => v.key === view)?.label}
				</span>
				{view === 'board' && !detailProject ? (
					<span className="wt-sectionhead__hint">{SECTION_HINT[view]}</span>
				) : (
					<SortMenu
						value={sortBy}
						options={SORT_OPTIONS}
						onChange={setSortBy}
					/>
				)}
				{/* New project — only where the space's binding can actually write
				    one, and only in the view where a project is the subject.
				    Keying off `projectService.writable` rather than the space name
				    is what keeps Work unchanged today and makes enabling it later a
				    service change rather than a component change. */}
				{view === 'project' && !detailProject && projectService.writable && (
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="New project"
						title="New project"
						onClick={() => setProjectForm(true)}>
						<i className="fa-sharp fa-solid fa-folder-plus" />
					</button>
				)}
				<button
					type="button"
					className="btn-cyv-teal wt-newbtn"
					onClick={() => setForm({})}>
					<i className="fa-sharp fa-solid fa-plus" />
					New task
				</button>
			</div>

			{/* Active surface */}
			{denied ? (
				<AccessNotice what="the full task board" />
			) : loading ? (
				<div className="wt-list">
					<div className="wt-skel" />
					<div className="wt-skel" />
					<div className="wt-skel" />
				</div>
			) : detailProject ? (
				<ProjectDetail
					project={detailProject}
					allTasks={tasks}
					childrenByParent={childrenByParent}
					showCompleted={showCompleted}
					projectsById={projectsById}
					now={now}
					sortBy={sortBy}
					onClose={() => setProjectDetailId(null)}
					rowHandlers={{
						onOpen: handleOpen,
						onAdvance: handleAdvance,
						onEdit: handleEdit,
						onReassign: handleReassign,
						onReprioritize: handleReprioritize,
						onSetDeadline: handleSetDeadline,
						onQuickUpdate: handleQuickUpdate,
						fields,
						offeredGates,
						changeFor,
						// Global display preference — the rows apply it to their
						// nested children; the roots arrived already filtered.
						showCompleted,
					}}
					onAddTask={handleAddToProject}
				/>
			) : view === 'board' ? (
				<BoardView
					roots={boardRoots}
					childrenByParent={childrenByParent}
					peopleInUse={peopleInUse}
					areasInUse={areasInUse}
					groupBy={boardGroupBy}
					setGroupBy={setBoardGroupBy}
					now={now}
					selected={cardProps}
					onSelectedChange={handleCardPropsChange}
					propertyCatalog={propertyCatalog}
					offeredGates={offeredGates}
					projectsById={projectsById}
					onMove={handleMove}
					onOpen={handleOpen}
					parkPrompt={parkPrompt}
					onParkChoose={handleParkChoose}
					onParkCancel={() => setParkPrompt(null)}
					showCompleted={showCompleted}
					onShowCompletedChange={handleShowCompletedChange}
				/>
			) : (
				<TaskListView
					mode={view}
					roots={visibleRoots}
					childrenByParent={childrenByParent}
					allTasks={tasks}
					projects={projects}
					projectsById={projectsById}
					now={now}
					sortBy={sortBy}
					handlers={{
						onOpen: handleOpen,
						onAdvance: handleAdvance,
						onEdit: handleEdit,
						onReassign: handleReassign,
						onReprioritize: handleReprioritize,
						onSetDeadline: handleSetDeadline,
						onQuickUpdate: handleQuickUpdate,
						fields,
						offeredGates,
						changeFor,
					}}
					onOpenProject={handleOpenProject}
					emptyState={emptyState}
					areaOrder={offeredAreas}
				/>
			)}
				</div>{/* .wt-main */}
			</div>{/* .wt-body */}

			{/* Mobile action bar — quick-add (one tap) + board toggle */}
			<div className="wt-actionbar">
				<button
					type="button"
					className="btn-cyv-teal wt-actionbar__add"
					onClick={() => setForm({})}>
					<i className="fa-sharp fa-solid fa-plus" />
					Quick add
				</button>
				<button
					type="button"
					className="btn-cyv-ghost wt-actionbar__board"
					aria-label="Toggle board"
					onClick={() => selectView(view === 'board' ? 'needs' : 'board')}>
					<i className="fa-sharp fa-solid fa-table-columns" />
				</button>
			</div>

			{/* Overlays */}
			{detailTask && (
				<TaskDetail
					task={detailTask}
					children={childrenByParent.get(detailTask.id) || []}
					showCompleted={showCompleted}
					projectsById={projectsById}
					now={now}
					onClose={() => setDetailId(null)}
					onEdit={handleEdit}
					onAdvance={handleAdvance}
					onAddSubtask={handleAddSubtask}
					onOpenChild={(c) => setDetailId(c.id)}
					fields={fields}
					onLinkStudent={handleLinkStudent}
				/>
			)}
			{form && (
				<TaskForm
					initial={form.initial}
					parentId={form.parentId}
					defaultProjectId={form.defaultProjectId}
					projects={projects}
					fields={fields}
					onClose={() => setForm(null)}
					onSaved={() => {}}
				/>
			)}
			{/* Guarded on `writable` here as well as at the button. The button is
			    the only way in today, but a state flag is not an authorization —
			    if a later change opens another path to setProjectForm, this stays
			    correct rather than rendering a form whose save can only fail. */}
			{projectForm && projectService.writable && (
				<ProjectForm
					offeredAreas={offeredAreas}
					onClose={() => setProjectForm(false)}
					onCreated={() => {}}
				/>
			)}
			{sheetOpen && (
				<SortFilterSheet
					sortBy={sortBy}
					setSortBy={setSortBy}
					sortOptions={SORT_OPTIONS}
					areasInUse={areasInUse}
					areaFilter={areaFilter}
					toggleArea={(a) => toggleSetItem(setAreaFilter, a)}
					peopleInUse={peopleInUse}
					assigneeFilter={assigneeFilter}
					toggleAssignee={(u) => toggleSetItem(setAssigneeFilter, u)}
					parkedOnly={parkedOnly}
					setParkedOnly={setParkedOnly}
					needsTriageOnly={needsTriageOnly}
					setNeedsTriageOnly={setNeedsTriageOnly}
					needsInfoOnly={needsInfoOnly}
					setNeedsInfoOnly={setNeedsInfoOnly}
					mailOnly={mailOnly}
					setMailOnly={setMailOnly}
					showCompleted={showCompleted}
					setShowCompleted={handleShowCompletedChange}
					showCount={openCount}
					onClose={() => setSheetOpen(false)}
				/>
			)}
			{pickerTask && (
				<StudentPicker
					onSelect={handleStudentSelected}
					onClose={() => setPickerTask(null)}
				/>
			)}
		</div>
	);
}

export default WorkTasksModule;
